import React, { useEffect, useState } from 'react';
import { Modal, Form, Select, InputNumber, DatePicker, Button, Alert } from 'antd';
import dayjs from 'dayjs';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';
import i18n from '../../../../i18n';
import axiosInstance from '../../../../variables/api/axiosInstance';
import { formatMontant } from '../../../../variables/constants';

export default function RembourserPrefinancementModal({ prefinancement, onClose, onSuccess }) {
    const { t } = useTranslation('dossierFinance');
    const [form] = Form.useForm();
    const [suggestion, setSuggestion] = useState(null);
    const [loading, setLoading] = useState(false);
    const [submitting, setSubmitting] = useState(false);

    useEffect(() => {
        if (!prefinancement) return;
        setLoading(true);
        axiosInstance.get(`/v1/prefinancements/${prefinancement.prefinancementTrackingId}/remboursement-suggere`)
            .then(res => {
                const s = res.data?.data;
                setSuggestion(s);
                if (s?.candidats?.length > 0) {
                    form.setFieldsValue({
                        encaissement: s.candidats[0].encaissementTrackingId,
                        montant: Number(s.montantSuggere),
                        date: dayjs(),
                    });
                }
            })
            .catch(() => toast.error(i18n.t('dossierFinance:prefinancements.rembourser.erreurSuggestion')))
            .finally(() => setLoading(false));
    }, [prefinancement, form]);

    if (!prefinancement) return null;

    const submit = async (vals) => {
        setSubmitting(true);
        try {
            await axiosInstance.post(`/v1/prefinancements/${prefinancement.prefinancementTrackingId}/rembourser`, {
                encaissementSourceTrackingId: vals.encaissement,
                montant: vals.montant,
                dateRemboursement: vals.date.format('YYYY-MM-DD'),
            });
            toast.success(t('prefinancements.rembourser.succes'));
            form.resetFields();
            onSuccess?.();
        } catch (e) {
            toast.error(e.response?.data?.message || t('prefinancements.rembourser.erreur'));
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <Modal title={t('prefinancements.rembourser.titre')}
            open={!!prefinancement} onCancel={onClose} footer={null} destroyOnClose width={520}>
            {suggestion && (
                <Alert type="info" showIcon style={{ marginBottom: 16 }}
                    message={t('prefinancements.rembourser.resteARembourser', { montant: formatMontant(suggestion.resteARembourser) })}
                    description={t('prefinancements.rembourser.montantSuggere', { montant: formatMontant(suggestion.montantSuggere) })} />
            )}
            <Form form={form} layout="vertical" onFinish={submit} disabled={loading}>
                <Form.Item name="encaissement" label={t('prefinancements.rembourser.encaissementSource')}
                    rules={[{ required: true, message: t('prefinancements.rembourser.encaissementRequis') }]}>
                    <Select placeholder={t('prefinancements.rembourser.choisirEncaissement')}
                        options={(suggestion?.candidats || []).map(c => ({
                            value: c.encaissementTrackingId,
                            label: t('prefinancements.rembourser.optionEncaissement', { numero: c.numeroCheque, solde: formatMontant(c.soldeDispoApresImputations) }),
                        }))} />
                </Form.Item>
                <Form.Item name="montant" label={t('prefinancements.rembourser.montant')}
                    rules={[{ required: true, message: t('prefinancements.rembourser.montantRequis') }]}>
                    <InputNumber style={{ width: '100%' }} min={1}
                        formatter={v => `${v}`.replace(/\B(?=(\d{3})+(?!\d))/g, ' ')}
                        parser={v => (v || '').replace(/\s/g, '')} />
                </Form.Item>
                <Form.Item name="date" label={t('prefinancements.rembourser.date')} rules={[{ required: true }]}>
                    <DatePicker style={{ width: '100%' }} format="DD/MM/YYYY" />
                </Form.Item>
                <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
                    <Button onClick={onClose}>{t('prefinancements.rembourser.annuler')}</Button>
                    <Button type="primary" htmlType="submit" loading={submitting}
                        style={{ background: '#059669', borderColor: '#059669' }}>
                        {t('prefinancements.rembourser.confirmer')}
                    </Button>
                </div>
            </Form>
        </Modal>
    );
}
